import { getTheme } from '@table-library/react-table-library/baseline';
import {
  CompactTable,
  CompactTableProps,
} from '@table-library/react-table-library/compact';
import { useTheme } from '@table-library/react-table-library/theme';
import { TableNode } from '@table-library/react-table-library/types';
import TableLoading from 'components/atoms/table/loading/TableLoading';
import TableMessage from 'components/atoms/table/message/TableMessage';
import { TableCustomColumnsButton } from 'components/molecules';
import { useElementFullyScrolled } from 'hooks';
import { debounce } from 'lodash';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChangeTableConfig, useTablesConfig } from 'store/state';
import { ILoadingProp, ISize } from 'types/components/components.interface';
import { MagellanColumn } from 'types/components/table/table.types';
import { getDefaultTableGridTemplateColumns } from 'utils/table/gridTemplateColumns.utils';
import './Table.scss';

export interface IMagellanTableProps<T extends TableNode>
  extends CompactTableProps<T>,
    ILoadingProp,
    ISize {
  emptyMessage?: ReactNode;
  onFullyScroll?: () => any;
  displayMode?: boolean;
  id?: string;
  overflowHidden?: boolean;
}

const MagellanTable = <T extends TableNode>({
  id,
  loading,
  emptyMessage,
  size = 'default',
  onFullyScroll,
  displayMode,
  overflowHidden=true,
  ...props
}: IMagellanTableProps<T>) => {
  const theme = useTheme(getTheme());
  const ref = useRef<HTMLTableElement>(null);
  const tablesConfig = useTablesConfig();
  const changeTableConfig = useChangeTableConfig();
  const { t } = useTranslation();
  const { isFullyScrolled } = useElementFullyScrolled(ref, {
    recheckTrigger: props.data.nodes.length,
  });
  const [hasVerticalScrollbar, setHasVerticalScrollbar] = useState(false);
  const isEmptyData = !props.data.nodes.length;

  const [layoutChange, setLayoutChange] = useState(() => {
    const defaultGrid = getDefaultTableGridTemplateColumns(
      props.columns as MagellanColumn<T>[]
    );

    if (id) {
      const storedGrid = tablesConfig[id]?.grid;
      return storedGrid ?? defaultGrid;
    }

    return defaultGrid;
  });

  const handleLayoutChange = useCallback(
    debounce((newGrid: string) => {
      setLayoutChange(newGrid);
      if (id) {
        changeTableConfig(id, { grid: newGrid });
      }
    }, 100),
    [id, changeTableConfig]
  );

  useEffect(() => {
    if (id && tablesConfig[id]?.grid) {
      setLayoutChange(tablesConfig[id].grid);
    }
  }, [id, tablesConfig]);

  useEffect(() => {
    if (isFullyScrolled && onFullyScroll) {
      onFullyScroll();
    }
  }, [isFullyScrolled]);

  const checkScrollbars = () => {
    if (ref.current) {
      const { scrollHeight, clientHeight } = ref.current;
      setHasVerticalScrollbar(scrollHeight > clientHeight);
    }
  };

  useEffect(() => {
    checkScrollbars();
  }, [props.data.nodes]);

  return (
    <div
      className={`magellan-table ${size} ${displayMode ? 'display-mode' : ''} ${
        hasVerticalScrollbar ? 'with-scroll' : ''
      } ${overflowHidden ? 'overflowHidden': ''}`}>
      <CompactTable
        ref={ref}
        theme={theme}
        minRows={0}
        {...props}
        layout={{
          fixedHeader: true,
          resizedLayout: layoutChange,
          onLayoutChange: handleLayoutChange,
          ...props.layout,
        }}
        data={props.data}
        tableOptions={{
          renderAfterTable: () => (
            <tfoot className='full-width-footer'>
              {!loading && isEmptyData && (
                <TableMessage emptyMessage={true}>
                  {emptyMessage ?? t('table.noData')}
                </TableMessage>
              )}
              {loading && <TableLoading />}
            </tfoot>
          ),
          ...props.tableOptions,
        }}
      />
    </div>
  );
};

MagellanTable.CustomColumnsButton = TableCustomColumnsButton;

export default MagellanTable;
